import { FinancialAccountPrimaryType, InvestmentHoldingsSyncOutcome } from '../../__generated__/globalTypes';

/** The Plaid product name as it appears in a connection's consent list. */
export const INVESTMENTS_PRODUCT = 'investments';

export interface IHoldingsSyncOutcomeInput {
  outcome: InvestmentHoldingsSyncOutcome;
  providerErrorCode?: string | null;
  accountsSynced?: number | null;
  holdingsRecorded?: number | null;
}

export interface IHoldingsSyncToast {
  type: 'success' | 'error';
  message: string;
}

export interface IConnectionAccount {
  type: FinancialAccountPrimaryType;
  /** Set when the household hid the account; the holdings step reads only visible accounts. */
  ignoredAt?: string | null;
}

/** Whether the connection holds a visible investment account, which is what the holdings step needs. */
export function hasInvestmentAccount(accounts: readonly IConnectionAccount[]): boolean {
  return accounts.some((account) => account.type === FinancialAccountPrimaryType.Investment && !account.ignoredAt);
}

/**
 * A connection whose recorded consent lacks investments while it holds an
 * investment account can only get holdings by relinking. Null consent means
 * no account fetch has recorded it yet, which is not a verdict.
 */
export function isInvestmentsRelinkCandidate(
  accounts: readonly IConnectionAccount[],
  consentedProducts: readonly string[] | null
): boolean {
  return (
    hasInvestmentAccount(accounts) && consentedProducts !== null && !consentedProducts.includes(INVESTMENTS_PRODUCT)
  );
}

/** The toast an operator reads after a forced holdings sync, naming the leg that refused. */
export function describeHoldingsSyncOutcome(result: IHoldingsSyncOutcomeInput): IHoldingsSyncToast {
  switch (result.outcome) {
    case InvestmentHoldingsSyncOutcome.Synced: {
      const accounts = result.accountsSynced ?? 0;
      const positions = result.holdingsRecorded ?? 0;
      return {
        type: 'success',
        message: `Holdings synced: ${accounts} ${accounts === 1 ? 'account' : 'accounts'}, ${positions} ${positions === 1 ? 'position' : 'positions'}.`,
      };
    }
    case InvestmentHoldingsSyncOutcome.NoInvestmentAccounts:
      return { type: 'error', message: 'No investment accounts on this connection.' };
    case InvestmentHoldingsSyncOutcome.NotConsented:
      return { type: 'error', message: 'Not consented to investments — relink candidate.' };
    case InvestmentHoldingsSyncOutcome.InstitutionLacksProduct:
      return { type: 'error', message: 'Institution does not offer investments.' };
    case InvestmentHoldingsSyncOutcome.ProductNotReady:
      return { type: 'error', message: 'Provider is still preparing holdings; its webhook follows.' };
    case InvestmentHoldingsSyncOutcome.ProviderRefused:
      return { type: 'error', message: `Provider refused holdings: ${result.providerErrorCode ?? 'unknown code'}.` };
    case InvestmentHoldingsSyncOutcome.NoConnection:
      return { type: 'error', message: 'Bank connection not found.' };
    default:
      return { type: 'error', message: `Holdings sync ended with ${String(result.outcome)}.` };
  }
}
